/**
 * Whether the open tab of a section the client thinks open has been answered
 * with the demo of a closed one (#2816) — a plan lowered with the page open:
 * the verdict is still `enabled`, the server already answers `demoOnly`. It
 * reports that up so the view draws the section as the demo.
 *
 * A component of its own, rendered only on an open screen, outside the demo
 * provider (the keys of an open section, `own`): the tab's keys ask the demo
 * rule, which a closed or unchecked screen never asks — as
 * `VoiceDemoStatusProbe`.
 */
import { useLayoutEffect } from 'react';
import { useStreamDemoFilter } from '../stream-filter/useStreamDemoFilter';
import { useVoiceDemoOnlyAnswered } from './useVoiceDemoOnly';
import type { VoiceSearch } from './voice-search';

export function VoiceDemoAnswerProbe({
  search,
  onSearchChange,
  onAnswered,
  onDeclines,
}: {
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  onAnswered: (demoOnly: boolean) => void;
  /** The requests decline the demo (`includeDemo=no`): the theme set's read says so too. */
  onDeclines: (declinesDemo: boolean) => void;
}) {
  const rule = useStreamDemoFilter(search, 'voice-section');
  const declinesDemo = rule.ready && rule.includeDemo === 'no';
  const demoOnly = useVoiceDemoOnlyAnswered(
    search,
    onSearchChange,
    search.audience ?? 'sales'
  );
  useLayoutEffect(() => {
    onAnswered(demoOnly);
    return () => onAnswered(false);
  }, [demoOnly, onAnswered]);
  useLayoutEffect(() => {
    onDeclines(declinesDemo);
    return () => onDeclines(false);
  }, [declinesDemo, onDeclines]);
  return null;
}
